import useAuth from 'features/auth/hooks/use-auth';
import { Box, Button, Grid, Typography } from '@mui/material';
import * as React from 'react';
import useEmailVerification from 'features/admin-email-verification/hooks/use-email-verification';
import { useState, useEffect } from 'react';
import { RESEND_EMAIL_VERIFICATION_INTERVAL_SECONDS } from 'features/admin-email-verification/constants/resend-email-verification-interval-seconds.const';

const STORAGE_KEY = 'email_verification_resend_unlock_time_at';
const EmailVerificationPrompt = () => {
  const [seconds, setSeconds] = useState<number>(0);
  const [buttonResendActive, setButtonResendActive] = useState<boolean>(true);
  const { state } = useAuth();
  const { sendEmailVerificationToken, emailVerificationState } =
    useEmailVerification();

  const handleResendVerificationEmail = () => {
    if (buttonResendActive) {
      sendEmailVerificationToken();
    }
  };

  useEffect(() => {
    setButtonResendActive(seconds <= 0);
  }, [seconds]);

  useEffect(() => {
    if (emailVerificationState.isVerificationSuccessfullySent) {
      const unlockTime =
        Date.now() + RESEND_EMAIL_VERIFICATION_INTERVAL_SECONDS * 1000;
      localStorage.setItem(STORAGE_KEY, unlockTime.toString());
      setSeconds(RESEND_EMAIL_VERIFICATION_INTERVAL_SECONDS);
    }
  }, [emailVerificationState.isVerificationSuccessfullySent]);

  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const storedUnlockTime = localStorage.getItem(STORAGE_KEY);
    if (storedUnlockTime) {
      const unlockTime = parseInt(storedUnlockTime, 10);
      const diff = Math.floor((unlockTime - Date.now()) / 1000);
      if (diff > 0) {
        setSeconds(diff);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (seconds > 0) {
      const interval = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            clearInterval(interval);
            localStorage.removeItem(STORAGE_KEY);
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [seconds]);

  return (
    <Grid height="100%" display="flex" alignItems="center">
      <Box
        width={'100%'}
        maxWidth={'700px'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        m="auto"
        gap={4}
      >
        <Typography textAlign={'center'} variant="h3">
          Please verify your email
        </Typography>
        <Typography textAlign={'center'} variant="body1">
          You`re almost there! We sent an email to{' '}
          <strong>{state.userEmail}</strong>
        </Typography>
        <Typography textAlign={'center'} variant="body1">
          Just click on the link in that email to complete your signup. If you
          don`t see it, you may need to check your spam folder.
        </Typography>
        <Typography textAlign={'center'} variant="body1">
          Still cant find the email? No problem.
        </Typography>
        <Box>
          {!buttonResendActive && (
            <Typography
              variant={'subtitle2'}
              color={'text.secondary'}
              mb={'5px'}
            >{`You can resend verification in ${formatTime(
              seconds
            )}`}</Typography>
          )}
          <Button
            size={'large'}
            variant={'contained'}
            onClick={handleResendVerificationEmail}
            disabled={
              !buttonResendActive || emailVerificationState.loadingStatus
            }
            sx={{ width: '100%' }}
          >
            {emailVerificationState.loadingStatus
              ? 'Sending...'
              : 'Resend Verification Email'}
          </Button>
        </Box>
      </Box>
    </Grid>
  );
};

export default EmailVerificationPrompt;
